import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';

import { IUserInfoDTO, ISystemSettingsDTO, IPreferencesDTO } from 'src/api/dtos';
import { IError } from 'src/api/types';

import {
  authenticate,
  fetchDefaultPreferences,
  fetchPreferences,
  fetchSystemSettings,
  fetchUserInfo,
  ssoAuthenticate,
  updatePreferences,
  updateUserInfo,
} from './user.actions';
import { IUserState } from './user.types';

const initialState: IUserState = {
  authenticated: false,
  info: null,
  settings: null,
  preferences: null,
  defaultPreferences: null,
  apiVersion: null,
  loading: false,
  submittingUserInfo: false,
  loadingPreferences: false,
  submittingPreferences: false,
  authenticatedError: null,
  infoError: null,
  settingsError: null,
  preferencesError: null,
  isInMaintenance: false,
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setAutoAuthenticated: (state: Draft<IUserState>) => {
      state.authenticated = true;
    },
    setIsInMaintenance: (state: Draft<IUserState>, { payload }: PayloadAction<boolean>) => {
      state.isInMaintenance = payload;
    },
  },
  extraReducers: (build) => {
    /* authenticate */
    build.addCase(authenticate.fulfilled.type, (state: Draft<IUserState>, { payload }: PayloadAction<boolean>) => {
      return {
        ...state,
        authenticated: payload,
        loading: false,
      };
    });
    build.addCase(authenticate.pending.type, (state: Draft<IUserState>) => {
      return {
        ...state,
        authenticated: false,
        authenticatedError: null,
        loading: true,
      };
    });
    build.addCase(authenticate.rejected.type, (state: Draft<IUserState>, { payload }: PayloadAction<IError>) => {
      return {
        ...state,
        authenticated: false,
        authenticatedError: payload,
        loading: false,
      };
    });
    /* ssoAuthenticate */
    build.addCase(ssoAuthenticate.fulfilled.type, (state: Draft<IUserState>, { payload }: PayloadAction<boolean>) => {
      return {
        ...state,
        authenticated: payload,
        loading: false,
      };
    });
    build.addCase(ssoAuthenticate.pending.type, (state: Draft<IUserState>) => {
      return {
        ...state,
        authenticated: false,
        authenticatedError: null,
        loading: true,
      };
    });
    build.addCase(ssoAuthenticate.rejected.type, (state: Draft<IUserState>, { payload }: PayloadAction<IError>) => {
      return {
        ...state,
        authenticated: false,
        authenticatedError: payload,
        loading: false,
      };
    });
    /* fetchUserInfo */
    build.addCase(
      fetchUserInfo.fulfilled.type,
      (state: Draft<IUserState>, { payload }: PayloadAction<IUserInfoDTO>) => {
        return {
          ...state,
          info: payload,
          loading: false,
        };
      },
    );
    build.addCase(fetchUserInfo.pending.type, (state: Draft<IUserState>) => {
      return {
        ...state,
        infoError: null,
        loading: true,
      };
    });
    build.addCase(fetchUserInfo.rejected.type, (state: Draft<IUserState>, { payload }: PayloadAction<IError>) => {
      return {
        ...state,
        infoError: payload,
        loading: false,
      };
    });
    /* updateUserInfo */
    build.addCase(
      updateUserInfo.fulfilled.type,
      (state: Draft<IUserState>, { payload }: PayloadAction<IUserInfoDTO>) => {
        return {
          ...state,
          info: payload,
          submittingUserInfo: false,
        };
      },
    );
    build.addCase(updateUserInfo.pending.type, (state: Draft<IUserState>) => {
      return {
        ...state,
        submittingUserInfo: true,
      };
    });
    build.addCase(updateUserInfo.rejected.type, (state: Draft<IUserState>) => {
      return {
        ...state,
        submittingUserInfo: false,
      };
    });
    /* fetchSystemSettings */
    build.addCase(
      fetchSystemSettings.fulfilled.type,
      (state: Draft<IUserState>, { payload }: PayloadAction<{ data: ISystemSettingsDTO; apiVersion: string }>) => {
        return {
          ...state,
          settings: payload.data,
          apiVersion: payload.apiVersion,
        };
      },
    );
    build.addCase(fetchSystemSettings.rejected.type, (state: Draft<IUserState>, { payload }: PayloadAction<IError>) => {
      return {
        ...state,
        settingsError: payload,
      };
    });
    /* fetchDefaultPreferences */
    build.addCase(
      fetchDefaultPreferences.fulfilled.type,
      (state: Draft<IUserState>, { payload }: PayloadAction<IPreferencesDTO>) => {
        return {
          ...state,
          defaultPreferences: payload,
        };
      },
    );
    /* fetchPreferences */
    build.addCase(
      fetchPreferences.fulfilled.type,
      (state: Draft<IUserState>, { payload }: PayloadAction<IPreferencesDTO>) => {
        return {
          ...state,
          loadingPreferences: false,
          preferences: payload,
        };
      },
    );
    build.addCase(fetchPreferences.pending.type, (state: Draft<IUserState>) => {
      return {
        ...state,
        loadingPreferences: true,
        preferencesError: null,
      };
    });
    build.addCase(fetchPreferences.rejected.type, (state: Draft<IUserState>, { payload }: PayloadAction<IError>) => {
      return {
        ...state,
        loadingPreferences: false,
        preferencesError: payload,
      };
    });
    /* updatePreferences */
    build.addCase(
      updatePreferences.fulfilled.type,
      (state: Draft<IUserState>, { payload }: PayloadAction<IPreferencesDTO>) => {
        return {
          ...state,
          preferences: payload,
          submittingPreferences: false,
        };
      },
    );
    build.addCase(updatePreferences.pending.type, (state: Draft<IUserState>) => {
      return {
        ...state,
        submittingPreferences: true,
      };
    });
    build.addCase(updatePreferences.rejected.type, (state: Draft<IUserState>) => {
      return {
        ...state,
        submittingPreferences: false,
      };
    });
  },
});

const { actions } = userSlice;

export const {
  setAutoAuthenticated: setAutoAuthenticatedActionCreator,
  setIsInMaintenance: setIsInMaintenanceActionCreator,
} = actions;
